import {
  HOST_QUERY_KEY,
  iamApiInstance,
  type ServiceAppState,
  TOKEN_SCOPE,
} from '@kc-console/context';
import { getConsoleBaseUrl, STATUS_CODE } from '@kc-console/core';

import { commonGuardErrorHandler } from '../../utils/commonGuardErrorHandler';
import { getClientIp } from '../../utils/getClientIp';
import { getRequestUrlData } from '../../utils/getRequestUrlData';

import type { AxiosError } from 'axios';
import type { Guard } from '../../types';

/**
 * 프로젝트용 토큰을 새로 발급 받습니다.
 * @description
 * - 이전 가드에서 결정된 targetProjectId로 프로젝트용 토큰을 새로 발급 받습니다.
 */
export const generateProjectTokenGuard: Guard<ServiceAppState> = async ({
  queryClient,
  state,
  context,
}) => {
  const { scope, token, projectId } = state;
  // project scope 서비스가 아니라면 필요없는 로직이므로 바로 가드 종료
  if (scope !== TOKEN_SCOPE.PROJECT) return { status: STATUS_CODE.OK };
  const clientIp = getClientIp(context);
  const { fetchCloudUserInfo } = iamApiInstance({
    token,
  });
  const { urlObj } = getRequestUrlData(context);
  const targetProjectId = projectId;

  try {
    const { authToken, user, project } = await queryClient.fetchQuery({
      queryKey: [HOST_QUERY_KEY.GET_IAM_ACCESS_TOKEN],
      queryFn: () => fetchCloudUserInfo({ projectId: targetProjectId, clientIp }),
    });

    return {
      token: authToken,
      domainId: user.domain?.id || '',
      domainName: user.domain?.name || '',
      defaultProjectId: user.default_project_id,
      projectName: project?.name || '',
      status: STATUS_CODE.OK,
      userId: user.id,
      userName: user.name,
    };
  } catch (error) {
    if ((error as AxiosError).status === STATUS_CODE.FORBIDDEN) {
      return {
        redirect: {
          destination: `${getConsoleBaseUrl(urlObj.origin)}/dashboard/welcome?bad_request_project=true`,
          permanent: false,
        },
      };
    }
    return commonGuardErrorHandler({ error, context, state });
  }
};

generateProjectTokenGuard.displayName = 'generateProjectTokenGuard';
generateProjectTokenGuard.traceCategory = 'authentication';
